/**
 * MaintenancePage
 *
 * Shown site-wide when site_mode = 'maintenance' in platform_settings.
 * Admin-managed via Settings → Site Mode.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const GOLD  = '#d39a21';
const BG    = '#0a0807';
const WHITE = '#f0ede6';
const MUTED = 'rgba(240,237,230,0.72)';
const GD    = "'Gilda Display', serif";
const NU    = "'Nunito', sans-serif";

export default function MaintenancePage() {
  const [logoUrl, setLogoUrl]   = useState('');
  const [message, setMessage]   = useState('');

  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key,value')
      .in('key', ['site_logo_url', 'maintenance_message'])
      .then(({ data }) => {
        (data || []).forEach(r => {
          if (r.key === 'site_logo_url')      setLogoUrl(r.value || '');
          if (r.key === 'maintenance_message') setMessage(r.value || '');
        });
      })
      .catch(() => {});
  }, []);

  // Inject admin-managed tracking scripts (GA4, StatCounter, ProvideSupport, etc.)
  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key,value')
      .eq('category', 'tracking')
      .then(({ data }) => {
        (data || []).forEach(({ key, value }) => {
          try {
            const { enabled, html } = JSON.parse(value || '{}');
            if (!enabled || !html?.trim()) return;
            if (document.querySelector(`[data-lwd-tracking-key="${key}"]`)) return;
            const wrap = document.createElement('div');
            wrap.innerHTML = html;
            wrap.querySelectorAll('script').forEach(src => {
              const s = document.createElement('script');
              Array.from(src.attributes).forEach(a => s.setAttribute(a.name, a.value));
              if (src.textContent?.trim()) s.textContent = src.textContent;
              s.setAttribute('data-lwd-tracking-key', key);
              document.head.appendChild(s);
            });
          } catch { /* fail silently */ }
        });
      })
      .catch(() => {});
  }, []);

  return (
    <div style={{
      position:       'fixed',
      inset:          0,
      zIndex:         9999,
      background:     BG,
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        '40px 24px',
      overflowY:      'auto',
      overflowX:      'hidden',
    }}>

      {/* ── Ambient glow ── */}
      <div style={{
        position:     'absolute', top: '20%', left: '50%',
        transform:    'translateX(-50%)', width: 'min(500px, 100vw)', height: 'min(500px, 100vw)',
        borderRadius: '50%',
        background:   `radial-gradient(circle, ${GOLD}0a 0%, transparent 70%)`,
        pointerEvents:'none',
      }} />

      {/* ── Grain ── */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.6, pointerEvents: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
      }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 580, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        {/* ── Logo ── */}
        <div style={{ marginBottom: 40 }}>
          {logoUrl
            ? <img src={logoUrl} alt="Logo" style={{ height: 44, objectFit: 'contain' }} />
            : <div style={{ fontFamily: GD, fontSize: 20, letterSpacing: '0.18em', color: GOLD, textTransform: 'uppercase' }}>Luxury Wedding Directory</div>
          }
        </div>

        {/* ── Icon ── */}
        <div style={{ marginBottom: 28 }}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="23" stroke={GOLD} strokeWidth="1.5" strokeOpacity="0.4" />
            <path d="M24 14v12" stroke={GOLD} strokeWidth="2" strokeLinecap="round"/>
            <circle cx="24" cy="32" r="1.5" fill={GOLD}/>
          </svg>
        </div>

        {/* ── Divider ── */}
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:36, width:'100%', maxWidth:340 }}>
          <div style={{ flex:1, height:1, background:`linear-gradient(to right, transparent, ${GOLD}44)` }} />
          <span style={{ color:GOLD, fontSize:12, opacity:0.6 }}>◈</span>
          <div style={{ flex:1, height:1, background:`linear-gradient(to left, transparent, ${GOLD}44)` }} />
        </div>

        {/* ── Headline ── */}
        <h1 style={{
          fontFamily: GD, fontSize: 'clamp(28px, 6vw, 52px)',
          fontWeight: 400, color: WHITE, textAlign: 'center',
          lineHeight: 1.2, margin: '0 0 16px',
        }}>
          We'll Be{' '}
          <span style={{ color: GOLD, fontStyle: 'italic' }}>Right Back</span>
        </h1>

        <p style={{
          fontFamily: NU, fontSize: 'clamp(13px, 2vw, 15px)',
          color: MUTED, textAlign: 'center', lineHeight: 1.75,
          margin: '0 0 12px', maxWidth: 420, fontWeight: 300,
        }}>
          {message || 'We are currently performing scheduled maintenance to improve your experience. We apologise for any inconvenience and will be back shortly.'}
        </p>

        {/* ── Divider ── */}
        <div style={{ display:'flex', alignItems:'center', gap:14, margin:'36px 0 24px', width:'100%', maxWidth:340 }}>
          <div style={{ flex:1, height:1, background:`linear-gradient(to right, transparent, rgba(211,154,33,0.18))` }} />
          <span style={{ color:GOLD, fontSize:10, opacity:0.6 }}>◈</span>
          <div style={{ flex:1, height:1, background:`linear-gradient(to left, transparent, rgba(211,154,33,0.18))` }} />
        </div>

        {/* ── Sign-off ── */}
        <div style={{ fontFamily: GD, fontSize: 14, color: 'rgba(240,237,230,0.68)', fontStyle: 'italic', marginBottom: 12 }}>
         , The LWD Editorial Team
        </div>

        {/* ── Footer ── */}
        <div style={{ fontFamily: NU, fontSize: 11, color: 'rgba(240,237,230,0.48)', letterSpacing: '0.1em' }}>
          © {new Date().getFullYear()} Luxury Wedding Directory
        </div>

      </div>
    </div>
  );
}
